import React from "react";

import { GridColumn, GridItem, GridRow } from "commons/ui/Grid";

import ExtensionsProporsionInfo from "components/subComponents/ExtensionsProporsionInfo";
import AttachmentPicker from "components/ui/AttachmentPicker";
import TitleFunctionsTopBlock from "components/ui/TitleFunctionsTopBlock";

import useCheckSectionErrors from "hooks/form/blocks/useCheckSectionErrors";
import useSectionTwoImages from "hooks/form/blocks/useSectionTwoImages";
import useTitleFunctionsTopBlock from "hooks/useTitleFunctionsTopBlock";

import { BlockEnum, DesktopOrMobile } from "types/enums";

function SectionTwoImagesForm({ blockId }: { blockId: BlockEnum }) {
    const { buttonDelete, setRightImage, setLeftImage, formHook } = useSectionTwoImages();

    useCheckSectionErrors({ formHook });

    const { checkErrorVisibility, form } = formHook;

    const titleFunctionsTopBlockHook = useTitleFunctionsTopBlock({
        buttonDelete: buttonDelete,
    });

    const desktopMobileText = React.useMemo(() => {
        if (blockId === BlockEnum.SectionTwoImagesVertical) {
            return {
                desktop: "1000x1500px oppure 1000x1000px (max 400KB)",
                mobile: "667x1000px oppure 667x667px (max 250KB)",
                proportion: "quadrata o verticale",
            };
        }

        return {
            bigImage: {
                desktop: "1500x844px (max 400KB)",
                mobile: "1200x 675px (max 250KB)",
            },
            smallImage: {
                desktop: "1000x1000px (max 400KB)",
                mobile: "667x667px (max 250KB)",
                proportion: "quadrata o verticale",
            },
        };
    }, [blockId]);

    return (
        <>
            <TitleFunctionsTopBlock titleFunctionsTopBlockHook={titleFunctionsTopBlockHook} />

            {titleFunctionsTopBlockHook.closedBlock && (
                <>
                    <GridColumn>
                        {blockId === BlockEnum.SectionTwoImagesVertical && (
                            <ExtensionsProporsionInfo
                                desktopText={desktopMobileText.desktop}
                                mobileText={desktopMobileText.mobile}
                                proportion={desktopMobileText.proportion}
                            />
                        )}

                        <GridRow>
                            <div className={"col-xxl-12-6 col-xl-12-12 col-xs-12-12"}>
                                <GridRow>
                                    <GridItem>
                                        <h3>Immagine a sinistra</h3>
                                    </GridItem>
                                </GridRow>

                                {blockId === BlockEnum.SectionTwoImagesRightBig && (
                                    <ExtensionsProporsionInfo
                                        desktopText={desktopMobileText.smallImage?.desktop}
                                        mobileText={desktopMobileText.smallImage?.mobile}
                                        proportion={desktopMobileText.smallImage?.proportion}
                                    />
                                )}

                                {blockId === BlockEnum.SectionTwoImagesLeftBig && (
                                    <ExtensionsProporsionInfo
                                        desktopText={desktopMobileText.bigImage?.desktop}
                                        mobileText={desktopMobileText.bigImage?.mobile}
                                        proportion={"16:9"}
                                    />
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Desktop"}
                                            setFile={(file) =>
                                                setLeftImage(file, DesktopOrMobile.desktop)
                                            }
                                            file={form.left_img.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("left_img.desktop")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Mobile"}
                                            setFile={(file) =>
                                                setLeftImage(file, DesktopOrMobile.mobile)
                                            }
                                            file={form.left_img.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("left_img.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>

                            <div className={"col-xxl-12-6 col-xl-12-12 col-xs-12-12"}>
                                <GridRow>
                                    <GridItem>
                                        <h3>Immagine a destra</h3>
                                    </GridItem>
                                </GridRow>

                                {blockId === BlockEnum.SectionTwoImagesRightBig && (
                                    <ExtensionsProporsionInfo
                                        desktopText={desktopMobileText.bigImage?.desktop}
                                        mobileText={desktopMobileText.bigImage?.mobile}
                                        proportion={"16:9"}
                                    />
                                )}

                                {blockId === BlockEnum.SectionTwoImagesLeftBig && (
                                    <ExtensionsProporsionInfo
                                        desktopText={desktopMobileText.smallImage?.desktop}
                                        mobileText={desktopMobileText.smallImage?.mobile}
                                        proportion={desktopMobileText.smallImage?.proportion}
                                    />
                                )}

                                <GridRow>
                                    <GridItem className={"col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Desktop"}
                                            setFile={(file) =>
                                                setRightImage(file, DesktopOrMobile.desktop)
                                            }
                                            file={form.right_img.desktop}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("right_img.desktop")}
                                        />
                                    </GridItem>

                                    <GridItem className={"col-xs-24-12"}>
                                        <AttachmentPicker
                                            buttonLabel={"Foto Mobile"}
                                            setFile={(file) =>
                                                setRightImage(file, DesktopOrMobile.mobile)
                                            }
                                            file={form.right_img.mobile}
                                            accept={".png,.jpg,.jpeg"}
                                            errors={checkErrorVisibility("right_img.mobile")}
                                        />
                                    </GridItem>
                                </GridRow>
                            </div>
                        </GridRow>
                    </GridColumn>
                </>
            )}
        </>
    );
}

export default SectionTwoImagesForm;
